import { Injectable, computed, effect, inject, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { settingStore } from '@core/store/setting.store';
import { environment } from '../../../environments/environment';
import { Tab } from '@shared/types';
import { profileStore } from '@core/store';



@Injectable({
  providedIn: 'root'
})
export class TabStateService {
  private router = inject(Router);
  private settingStore = inject(settingStore);
  private profileStore = inject(profileStore);
  private readonly MAX_TABS = environment.maxTabs;

  private contextKey = computed(() => {
    const tenantId = this.profileStore.data.activeTenantId();
    const condominiumId = this.profileStore.data.activeCondominiumId();
    const tenantPart = tenantId || 'noneTenant';
    const condoPart = condominiumId || 'none';
    return `${tenantPart}::${condoPart}`;
  });

  // Computed signals that read directly from store (by contextKey)
  getTabs = computed(() => {
    const key = this.contextKey();
    const map = this.settingStore.data.openTabsByContext();
    return map[key] || [];
  });
  getActiveIndex = computed(() => {
    const key = this.contextKey();
    const map = this.settingStore.data.activeTabIndexByContext();
    return map[key] ?? -1;
  });

  constructor() {
    // React to condominium context changes: ensure context exists and restore route.
    effect(() => {
      const key = this.contextKey();
      untracked(() => this.onContextChanged(key));
    });
  }

  private onContextChanged(contextKey: string): void {
    const tabsByContext = this.settingStore.data.openTabsByContext();
    const activeByContext = this.settingStore.data.activeTabIndexByContext();

    // Ensure entries exist for current context
    if (!(contextKey in tabsByContext)) {
      this.settingStore.setOpenTabsForContext(contextKey, []);
    }
    if (!(contextKey in activeByContext)) {
      this.settingStore.setActiveTabIndexForContext(contextKey, -1);
    }

    // Restore navigation for this context
    const nextTabs = this.settingStore.data.openTabsByContext()[contextKey] || [];
    const nextActive = this.settingStore.data.activeTabIndexByContext()[contextKey] ?? -1;

    if (nextTabs.length > 0) {
      const idx = nextActive >= 0 && nextActive < nextTabs.length ? nextActive : 0;
      const route = nextTabs[idx]?.route;
      if (route && this.router.url !== route) {
        this.router.navigate([route]);
      }
      return;
    }

    // Default route when no tabs for this condo
    const dashboardUrl = `/admin/condominium/dashboard`;
    if (this.router.url !== dashboardUrl) {
      this.router.navigateByUrl(dashboardUrl);
    }
  }

  openTab(config: {
    route: string;
    title: string;
    icon?: string;
    data?: any;
    closable?: boolean;
  }): void {
    const { route, title, icon, data, closable = true } = config;

    // Check if tab already exists
    const currentTabs = this.getTabs();
    const existingIndex = currentTabs.findIndex(t => t.route === route);

    if (existingIndex !== -1) {
      // Tab exists, just focus it
      this.setActiveTab(existingIndex);
      return;
    }

    // Check if we've reached the maximum number of tabs
    if (currentTabs.length >= this.MAX_TABS) {
      console.warn(`Máximo de ${this.MAX_TABS} tabs alcanzado`);
      return;
    }

    // Create new tab
    const newTab: Tab = {
      id: this.generateTabId(),
      title,
      route,
      icon,
      data,
      closable,
      loaded: false
    };

    // Update store directly
    const updatedTabs = [...currentTabs, newTab];
    this.settingStore.setOpenTabsForContext(this.contextKey(), updatedTabs);
    this.settingStore.setActiveTabIndexForContext(this.contextKey(), updatedTabs.length - 1);

    // Navigate to the route
    this.router.navigate([route]);
  }

  closeTab(index: number): void {
    const currentTabs = this.getTabs();
    const currentActiveIndex = this.getActiveIndex();

    const wasActive = index === currentActiveIndex;

    // Remove tab from array
    const updatedTabs = currentTabs.filter((_, i) => i !== index);
    this.settingStore.setOpenTabsForContext(this.contextKey(), updatedTabs);

    // Adjust active index
    if (updatedTabs.length === 0) {
      // No tabs left, set active index to -1
      this.settingStore.setActiveTabIndexForContext(this.contextKey(), -1);
    } else if (wasActive) {
      const newIndex = Math.min(index, updatedTabs.length - 1);
      this.setActiveTab(newIndex);
    } else if (index < currentActiveIndex) {
      this.settingStore.setActiveTabIndexForContext(this.contextKey(), currentActiveIndex - 1);
    }
  }

  setActiveTab(index: number): void {
    const currentTabs = this.getTabs();
    if (index === -1) {
      // No active tab
      this.settingStore.setActiveTabIndexForContext(this.contextKey(), -1);
    } else if (index >= 0 && index < currentTabs.length) {
      this.settingStore.setActiveTabIndexForContext(this.contextKey(), index);
      const tab = currentTabs[index];
      this.router.navigate([tab.route]);
    }
  }

  closeTabByRoute(route: string): void {
    const currentTabs = this.getTabs();
    route = "/" + route;
    const index = currentTabs.findIndex(t => t.route === route);
    if (index !== -1) {
      this.closeTab(index);
    }
  }

  closeOtherTabs(): void {
    const currentTabs = this.getTabs();
    const currentActiveIndex = this.getActiveIndex();
    const activeTab = currentTabs[currentActiveIndex];
    this.settingStore.setOpenTabsForContext(this.contextKey(), [activeTab]);
    this.settingStore.setActiveTabIndexForContext(this.contextKey(), 0);
  }

  closeTabsToRight(index: number): void {
    const currentTabs = this.getTabs();
    const currentActiveIndex = this.getActiveIndex();
    const updatedTabs = currentTabs.slice(0, index + 1);
    this.settingStore.setOpenTabsForContext(this.contextKey(), updatedTabs);
    if (currentActiveIndex > index) {
      this.settingStore.setActiveTabIndexForContext(this.contextKey(), index);
    }
  }

  closeTabsToLeft(index: number): void {
    const currentTabs = this.getTabs();
    const currentActiveIndex = this.getActiveIndex();

    if (index === 0) {
      return; // No tabs to close on the left
    }

    const updatedTabs = currentTabs.slice(index);
    this.settingStore.setOpenTabsForContext(this.contextKey(), updatedTabs);

    // Adjust active index
    if (currentActiveIndex < index) {
      // Active tab was closed, activate the tab at the new index 0
      this.setActiveTab(0);
    } else {
      // Active tab moved left by 'index' positions
      this.settingStore.setActiveTabIndexForContext(this.contextKey(), currentActiveIndex - index);
    }
  }

  closeAllTabs(): void {
    const currentTabs = this.getTabs();

    // Close all tabs, even non-closable ones
    this.settingStore.setOpenTabsForContext(this.contextKey(), []);
    this.settingStore.setActiveTabIndexForContext(this.contextKey(), -1);
  }

  previousTab(): void {
    const currentActiveIndex = this.getActiveIndex();
    const currentTabs = this.getTabs();
    const newIndex = currentActiveIndex > 0
      ? currentActiveIndex - 1
      : currentTabs.length - 1;
    this.setActiveTab(newIndex);
  }

  nextTab(): void {
    const currentActiveIndex = this.getActiveIndex();
    const currentTabs = this.getTabs();
    const newIndex = currentActiveIndex < currentTabs.length - 1
      ? currentActiveIndex + 1
      : 0;
    this.setActiveTab(newIndex);
  }

  updateTabTitle(route: string, newTitle: string): void {
    const currentTabs = this.getTabs();
    const updatedTabs = currentTabs.map(tab =>
      tab.route === route ? { ...tab, title: newTitle } : tab
    );
    this.settingStore.setOpenTabsForContext(this.contextKey(), updatedTabs);
  }

  // Utility methods
  hasTab(route: string): boolean {
    const currentTabs = this.getTabs();
    return currentTabs.some(tab => tab.route === route);
  }

  clearAllTabs(): void {
    this.settingStore.setOpenTabsForContext(this.contextKey(), []);
    this.settingStore.setActiveTabIndexForContext(this.contextKey(), -1);
  }

  reorderTabs(previousIndex: number, currentIndex: number): void {
    const currentTabs = this.getTabs();
    const currentActiveIndex = this.getActiveIndex();

    if (previousIndex === currentIndex) {
      return; // No change needed
    }

    // Create a copy of the tabs array
    const reorderedTabs = [...currentTabs];

    // Move the tab from previousIndex to currentIndex
    const [movedTab] = reorderedTabs.splice(previousIndex, 1);
    reorderedTabs.splice(currentIndex, 0, movedTab);

    // Update the store with the new order
    this.settingStore.setOpenTabsForContext(this.contextKey(), reorderedTabs);

    // Adjust the active index if the active tab was moved
    if (currentActiveIndex === previousIndex) {
      // The active tab was moved, update its index
      this.settingStore.setActiveTabIndexForContext(this.contextKey(), currentIndex);
    } else if (previousIndex < currentActiveIndex && currentIndex >= currentActiveIndex) {
      // Active tab was shifted left
      this.settingStore.setActiveTabIndexForContext(this.contextKey(), currentActiveIndex - 1);
    } else if (previousIndex > currentActiveIndex && currentIndex <= currentActiveIndex) {
      // Active tab was shifted right
      this.settingStore.setActiveTabIndexForContext(this.contextKey(), currentActiveIndex + 1);
    }
  }

  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
